import type { PermissionEnum, UserResponse } from "@sidegigllc/api/client";
import { useAuth } from "domains/auth";

/** Capability value strings mirrored from `api/authorization/capabilities.py` (Capability.value). */
export const SupportOpsCapability = {
  mergeUsers: "support_ops_merge_users",
  updateSupertoken: "support_ops_update_supertoken",
  rerunStats: "support_ops_rerun_stats",
  removeSuspensions: "support_ops_remove_suspensions",
  createEmployeeInterview: "support_ops_create_employee_interview",
  deleteStripe: "support_ops_delete_stripe",
  rectifyZeal: "support_ops_rectify_zeal",
  lockGigs: "support_ops_lock_gigs",
  bulkSetCertificates: "support_ops_bulk_set_certificates",
  addProToShift: "support_ops_add_pro_to_shift",
  changeShiftLocations: "support_ops_change_shift_locations",
  changeRating: "support_ops_change_rating",
  changeProName: "support_ops_change_pro_name",
  addManagersToLocations: "support_ops_add_managers_to_locations",
  changeShiftAddress: "support_ops_change_shift_address",
  backPayPro: "back_pay_pro",
  setProTierBadge: "support_ops_set_pro_tier_badge",
  logOutAllDevices: "support_ops_log_out_all_devices",
} as const;

export const MANAGE_PERMISSIONS = "manage_permissions";

export const MANAGE_GIGPRO_ADMIN = "manage_gigpro_admin";

/** The grantable Support Ops features, one per capability — drives the Admin Permissions page. */
export const SUPPORT_OPS_FEATURES: ReadonlyArray<{ permission: PermissionEnum; label: string; note?: string }> = [
  { permission: SupportOpsCapability.mergeUsers, label: "Merge Users" },
  { permission: SupportOpsCapability.updateSupertoken, label: "Update Supertoken" },
  { permission: SupportOpsCapability.rerunStats, label: "Rerun Stats" },
  { permission: SupportOpsCapability.removeSuspensions, label: "Remove Suspensions" },
  { permission: SupportOpsCapability.createEmployeeInterview, label: "Create Employee Interview" },
  { permission: SupportOpsCapability.deleteStripe, label: "Delete Stripe Account" },
  { permission: SupportOpsCapability.rectifyZeal, label: "Rectify Zeal" },
  { permission: SupportOpsCapability.lockGigs, label: "Lock Gigs" },
  { permission: SupportOpsCapability.bulkSetCertificates, label: "Bulk Set Certificates" },
  { permission: SupportOpsCapability.addProToShift, label: "Add Pro to Shift" },
  { permission: SupportOpsCapability.changeShiftLocations, label: "Change Shift Locations" },
  { permission: SupportOpsCapability.changeRating, label: "Change Rating" },
  { permission: SupportOpsCapability.changeProName, label: "Change Pro First/Last Name" },
  { permission: SupportOpsCapability.addManagersToLocations, label: "Add Managers to Locations" },
  { permission: SupportOpsCapability.changeShiftAddress, label: "Change Shift Address" },
  { permission: SupportOpsCapability.backPayPro, label: "Back Pay Pro", note: "On pro profile" },
  { permission: SupportOpsCapability.setProTierBadge, label: "Pro Tier Medal" },
  { permission: SupportOpsCapability.logOutAllDevices, label: "Log Out of All Devices" },
];

export const MANAGEMENT_FEATURES: ReadonlyArray<{ permission: PermissionEnum; label: string }> = [
  { permission: MANAGE_PERMISSIONS, label: "Manage Permissions" },
  { permission: MANAGE_GIGPRO_ADMIN, label: "Manage Gigpro Admin" },
];

export const userHasCapability = (user: UserResponse | undefined, value: string): boolean =>
  user?.capabilities?.includes(value) ?? false;

/** The held Capability.value strings from `/me`, resolved self-only (empty for other users). */
export function useHeldCapabilities(): ReadonlySet<string> {
  const capabilities = useAuth().user?.capabilities;
  return new Set(capabilities ?? []);
}

export function useHasCapability(value: string): boolean {
  return userHasCapability(useAuth().user, value);
}

export function hasAnySupportOpsCapability(held: ReadonlySet<string>): boolean {
  return SUPPORT_OPS_FEATURES.some(
    (feature) => feature.permission !== SupportOpsCapability.backPayPro && held.has(feature.permission),
  );
}

// Gates panel visibility: the `/me` platform-admin bundle excludes these, so this trips only
// for a grantee, which is what reveals the panel after an explicit grant.
export function useHasAnySupportOpsCapability(): boolean {
  const held = useHeldCapabilities();
  return hasAnySupportOpsCapability(held);
}
